import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { loadModules } from 'esri-loader';

export const useImageStore = create(
  persist(
    (set, get) => ({
      // State
      imageFile: null,
      imageUrl: null,
      imageData: null, // Base64 data for persistence
      opacity: 0.8,
      rotation: 0,
      extent: null,
      isImageLoaded: false,

      // Actions
      setImageFile: (file) => {
        set({ imageFile: file, isImageLoaded: true });
      },

      setImageUrl: (url) => {
        // Clean up previous URL if it exists
        const currentUrl = get().imageUrl;
        if (currentUrl && currentUrl !== url && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        set({ imageUrl: url });
      },

      setImageData: (data) => {
        set({ imageData: data });
      },

      setOpacity: (opacity) => {
        set({ opacity });
      },

      setRotation: (rotation) => {
        set({ rotation });
      },

      setExtent: (extent) => {
        // Convert extent to serializable format for storage
        let serializedExtent = null;
        if (extent) {
          serializedExtent = {
            xmin: extent.xmin,
            ymin: extent.ymin,
            xmax: extent.xmax,
            ymax: extent.ymax,
            spatialReference: extent.spatialReference
              ? {
                  wkid: extent.spatialReference.wkid,
                  latestWkid: extent.spatialReference.latestWkid,
                }
              : null,
          };
        }
        set({ extent: serializedExtent });
      },

      resetImage: () => {
        const currentUrl = get().imageUrl;
        if (currentUrl && currentUrl.startsWith('blob:')) {
          URL.revokeObjectURL(currentUrl);
        }
        set({
          imageFile: null,
          imageUrl: null,
          imageData: null,
          opacity: 0.8,
          rotation: 0,
          extent: null,
          isImageLoaded: false,
        });
      },

      updateImageSettings: (settings) => {
        set({ ...settings });
      },

      // Utility functions
      getImageState: () => {
        const state = get();
        return {
          imageFile: state.imageFile,
          imageUrl: state.imageUrl,
          imageData: state.imageData,
          opacity: state.opacity,
          rotation: state.rotation,
          extent: state.extent,
          isImageLoaded: state.isImageLoaded,
        };
      },

      hasImage: () => {
        const state = get();
        return !!(state.imageFile || state.imageUrl || state.imageData);
      },

      hasPersistedImage: () => {
        const state = get();
        return !!(state.imageData && state.extent);
      },
    }),
    {
      name: 'image-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        // Persist these values across sessions
        opacity: state.opacity,
        rotation: state.rotation,
        extent: state.extent,
        isImageLoaded: state.isImageLoaded,
        imageData: state.imageData, // Store base64 data
        // Store file metadata but not the actual file
        fileMetadata: state.imageFile
          ? {
              name: state.imageFile.name,
              size: state.imageFile.size,
              type: state.imageFile.type,
              lastModified: state.imageFile.lastModified,
            }
          : null,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.log('Error rehydrating image store:', error);
          } else if (state) {
            // Clear non-persistable data on rehydration
            state.imageFile = null;
            state.imageUrl = null;

            // If we have base64 data and metadata, mark as needing reload
            if (state.imageData && state.fileMetadata) {
              state.isImageLoaded = true;
            } else {
              state.isImageLoaded = false;
            }
          }
        };
      },
    }
  )
);

// Helper functions for managing image state across components
export const imageStoreHelpers = {
  // Check if there's a stored image configuration
  hasStoredImageConfig: () => {
    const stored = sessionStorage.getItem('image-store');
    if (!stored) return false;

    try {
      const parsedState = JSON.parse(stored);
      return !!(
        parsedState.state &&
        (parsedState.state.fileMetadata || parsedState.state.imageData)
      );
    } catch {
      return false;
    }
  },

  // Get stored image metadata
  getStoredImageMetadata: () => {
    const stored = sessionStorage.getItem('image-store');
    if (!stored) return null;

    try {
      const parsedState = JSON.parse(stored);
      return parsedState.state?.fileMetadata || null;
    } catch {
      return null;
    }
  },

  // Get stored image configuration including extent and transformations
  getStoredImageConfig: () => {
    const stored = sessionStorage.getItem('image-store');
    if (!stored) return null;

    try {
      const parsedState = JSON.parse(stored);
      return parsedState.state || null;
    } catch {
      return null;
    }
  },

  // Check if we have stored base64 image data
  hasStoredImageData: () => {
    const stored = sessionStorage.getItem('image-store');
    if (!stored) return false;

    try {
      const parsedState = JSON.parse(stored);
      return !!(parsedState.state && parsedState.state.imageData);
    } catch {
      return false;
    }
  },

  // Get stored base64 image data
  getStoredImageData: () => {
    const stored = sessionStorage.getItem('image-store');
    if (!stored) return null;

    try {
      const parsedState = JSON.parse(stored);
      return parsedState.state?.imageData || null;
    } catch {
      return null;
    }
  },

  // Reconstruct Extent object from stored data
  reconstructExtent: async (storedExtent) => {
    if (!storedExtent) return null;

    try {
      const [Extent] = await loadModules(['esri/geometry/Extent']);

      return new Extent({
        xmin: storedExtent.xmin,
        ymin: storedExtent.ymin,
        xmax: storedExtent.xmax,
        ymax: storedExtent.ymax,
        spatialReference: storedExtent.spatialReference || { wkid: 4326 },
      });
    } catch (error) {
      console.error('Error reconstructing extent:', error);
      return null;
    }
  },

  // Clear all image data including persisted data
  clearAllImageData: () => {
    const store = useImageStore.getState();
    store.resetImage();
    sessionStorage.removeItem('image-store');
  },

  // Restore image from session storage
  restoreImageFromStorage: async () => {
    const config = imageStoreHelpers.getStoredImageConfig();
    if (!config) return null;

    const store = useImageStore.getState();

    // Restore basic settings
    if (config.opacity !== undefined) store.setOpacity(config.opacity);
    if (config.rotation !== undefined) store.setRotation(config.rotation);

    // Restore extent if available
    if (config.extent) {
      const extent = await imageStoreHelpers.reconstructExtent(config.extent);
      if (extent) {
        store.setExtent(extent);
      }
    }

    // Restore image data if available
    if (config.imageData) {
      store.setImageData(config.imageData);
    }

    return {
      imageData: config.imageData,
      metadata: config.fileMetadata,
      opacity: config.opacity,
      rotation: config.rotation,
      extent: config.extent,
    };
  },

  // Get storage size info
  getStorageInfo: () => {
    const stored = sessionStorage.getItem('image-store');
    if (!stored) return { size: 0, hasData: false };

    const sizeInBytes = new Blob([stored]).size;
    const sizeInKB = (sizeInBytes / 1024).toFixed(2);
    const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);

    return {
      size: sizeInBytes,
      sizeKB: sizeInKB,
      sizeMB: sizeInMB,
      hasData: sizeInBytes > 0,
      formatted: sizeInBytes > 1024 * 1024 ? `${sizeInMB} MB` : `${sizeInKB} KB`,
    };
  },

  // Validate stored data integrity
  validateStoredData: () => {
    const config = imageStoreHelpers.getStoredImageConfig();
    if (!config) return { valid: false, issues: ['No stored configuration'] };

    const issues = [];

    if (!config.imageData && !config.fileMetadata) {
      issues.push('No image data or metadata found');
    }

    if (config.imageData && !config.imageData.startsWith('data:image/')) {
      issues.push('Invalid image data format');
    }

    if (
      config.extent &&
      (!config.extent.xmin || !config.extent.xmax || !config.extent.ymin || !config.extent.ymax)
    ) {
      issues.push('Invalid extent data');
    }

    if (config.opacity !== undefined && (config.opacity < 0 || config.opacity > 1)) {
      issues.push('Invalid opacity value');
    }

    if (config.rotation !== undefined && (config.rotation < 0 || config.rotation >= 360)) {
      issues.push('Invalid rotation value');
    }

    return {
      valid: issues.length === 0,
      issues,
      config,
    };
  },
};
